import { ActionType, TogglePolygonDrawAction } from '@/actions/actiontypes';
import { addPointToMap, zoomToExtent } from '@/actions/mapActions';
import {
    getVSRForCoordinateAndZoom,
    getVSRForLegacyVegreferanse,
    getVSRForVeglenkesekvensAndZoom,
} from '@/actions/overlayActions';
import {
    addKommuneToSearch,
    addPolygonToSearch,
    addRiksvegruteToSearch,
    addVSRToSearch,
} from '@/actions/omraderActions';
import { addLinkQuery } from '@/actions/valgtActions';
import {
    addVegobjektToSearch,
    addVegobjekttypeToSearch,
} from '@/actions/vegobjekterActions';
import { vegobjekttypeToEntry } from '@/components/avgrensning/search/useSearchResult';
import { Polygon } from '@/domain/omrader/Polygon';
import OLPolygon from 'ol/geom/Polygon';
import { Vegkategori } from '@/domain/Vegkategori';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { WKTObject } from '@/domain/WKTObject';
import { FeatureFlag, flags } from '@/featureFlags';
import { stateUser } from '@/selectors/selectors';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { Dispatch, useSelector } from '@/state/store';
import { parseLegacyVegreferanse } from '@/utils/LegacyVegreferanseParser';
import { kartutsnittFromWktStrings } from '@/utils/SpatialHelper';
import { isLinkQuery } from '@/utils/Utils';
import { createVegrefParser } from '@/utils/VegrefParser';
import { Position } from 'geojson';
import proj4 from 'proj4';
import { useContext, useMemo } from 'react';
import * as uuidgen from 'uuid';
import { LegacyVegreferanse } from '@/domain/LegacyVegreferanse';
import { Kommune, Riksvegrute, Vegsystemreferanse } from '@/domain/omrader';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';

export type SearchSuggestion = {
    title: string;
    description: string;
    action: (dispatch: Dispatch) => void | Promise<void>;
    disabled?: boolean;
    searchText?: string;
    pendingText?: string;
};

enum Projection {
    UTM33,
    WGS84,
}

const extractCoordinateOrdinateTexts = (
    searchText: string
): [string, string] | undefined => {
    const normalizedSearchText = searchText.replace(
        /^\s*POINT(?:\s+ZM|\s+Z|\s+M)?\s*/i,
        ''
    ); // Støtter WKT POINT, POINT Z, POINT M og POINT ZM.

    const parts = normalizedSearchText
        .replace(/[()]/g, ' ')
        .trim()
        .split(/[\s,]+/)
        .filter(Boolean);
    if (parts.length < 2) return undefined;

    const [first, second] = parts;
    if (!Number.isFinite(Number(first)) || !Number.isFinite(Number(second)))
        return undefined;

    return [first, second];
};

export function parseCoordinates(searchText: string) {
    const ordinateTexts = extractCoordinateOrdinateTexts(searchText);
    if (!ordinateTexts) throw new Error('Invalid coordinate input');

    let coordinates = ordinateTexts.map(Number) as Position;

    const projection = coordinates.every(inWgs84Range)
        ? Projection.WGS84
        : Projection.UTM33;

    coordinates = reorderCoordinates(coordinates, projection);
    if (projection === Projection.WGS84) coordinates = projectWGS84ToUTM33(coordinates);

    return {
        north: coordinates[0],
        east: coordinates[1],
    };
}

export const useSearchSuggestion = (searchText: string): SearchSuggestion[] => {
    const { data } = useContext(ConfigAndDataContext);
    const { isLoggedIn } = useSelector(stateUser);
    const kommuneByNummer = useMemo(
        () =>
            data.kommuner.reduce<Record<string, Kommune>>(
                (set, kommune) => ({ ...set, [kommune.nummer]: kommune }),
                {}
            ),
        [data.kommuner]
    );
    const riksvegruterByEnumid = useMemo(
        () =>
            data.riksvegruter.reduce<Record<string, Riksvegrute>>(
                (set, riksvegrute) => ({
                    ...set,
                    [riksvegrute.enumid]: riksvegrute,
                }),
                {}
            ),
        [data.riksvegruter]
    );
    const vegsystemreferanseParser = useMemo(
        () => createVegrefParser(data.datakatalog.getTypeById(915)),
        [data]
    );

    const result: SearchSuggestion[] = [];
    if (flags().has(FeatureFlag.POLYGONS)) {
        const searchSuggestionPolygon = createSearchSuggestionPolygon(searchText);
        if (searchSuggestionPolygon) result.push(searchSuggestionPolygon);
    }
    const vegsystemreferanse = vegsystemreferanseParser(searchText.trim());
    if (!!vegsystemreferanse) {
        result.push(
            createSearchSuggestionVegsystemreferanse(searchText, vegsystemreferanse)
        );
    }
    const legacyVegreferanse = parseLegacyVegreferanse(searchText);
    if (legacyVegreferanse)
        result.push(createSearchSuggestionHistoricalVegreferanse(legacyVegreferanse));
    if (isPointLinkQuery(searchText))
        result.push(createSearchSuggestionVegsystemreferanseFromLink(searchText));
    if (isLinkQuery(searchText)) result.push(createSearchSuggestionLinkQuery(searchText));

    if (isNumber(searchText)) {
        const vegobjekttype = data.datakatalog.getTypeById(Number.parseInt(searchText));
        const kommune = kommuneByNummer[searchText];
        const riksvegrute = riksvegruterByEnumid[searchText];
        if (vegobjekttype) {
            result.push(
                createSearchSuggestionVegobjekttype(searchText, vegobjekttype, isLoggedIn)
            );
        }
        if (kommune) {
            result.push(createSearchSuggestionKommune(searchText, kommune));
        }
        if (riksvegrute) {
            result.push(createSearchSuggestionRiksvegrute(searchText, riksvegrute));
        }
        result.push(createSearchSuggestionVegobjekt(searchText));
    }
    if (isCoordinate(searchText)) {
        result.push(createSearchSuggestionVegsystemreferanseFromCoordinate(searchText));
        result.push(createSearchSuggestionCoordinate(searchText));
    }
    return result;
};

export const createSearchSuggestionPolygon = (
    searchText: string
): SearchSuggestion | undefined => {
    if (/^\s*POLYGON(?:\s+Z)?\s*\(/i.test(searchText)) {
        try {
            const geometry = Polygon.serializer.readGeometry(searchText);
            if (!(geometry instanceof OLPolygon)) return undefined;
            const hasHoles = geometry.getLinearRingCount() > 1;
            const wkt = hasHoles
                ? Polygon.serializer.writeGeometry(
                      new OLPolygon(
                          geometry.getCoordinates().slice(0, 1),
                          geometry.getLayout()
                      )
                  )
                : searchText;
            const polygon = new Polygon(new WKTObject(6173, wkt));
            polygon.toParams();
            return {
                title: hasHoles ? 'Polygonsøk (hull ignoreres)' : 'Polygonsøk',
                description: 'Bruk Polygon WKT for å filtrere på: ' + wkt,
                searchText: searchText,
                action: dispatch => {
                    dispatch(addPolygonToSearch(polygon));
                },
            };
        } catch {}
    }
    if (searchText.length > 1 && 'polygon'.startsWith(searchText.toLowerCase())) {
        return {
            title: 'Polygonsøk',
            description: 'Tegn et polygon for å filtrere i kartet',
            searchText: searchText,
            action: dispatch => {
                dispatch({
                    type: ActionType.TOGGLE_POLYGON_DRAW,
                    drawing: true,
                } as TogglePolygonDrawAction);
            },
        };
    }
};

export const createSearchSuggestionVegsystemreferanse = (
    searchText: string,
    vegsystemreferanse: Vegsystemreferanse
): SearchSuggestion => {
    if (vegsystemreferanse instanceof Vegkategori) {
        return {
            title: vegsystemreferanse.navn,
            description: 'Vegsystemreferanse',
            searchText: searchText,
            action: dispatch => {
                dispatch(addVSRToSearch(vegsystemreferanse));
            },
        };
    } else {
        return {
            title: vegsystemreferanse.kommune
                ? `${vegsystemreferanse.kommune} ${vegsystemreferanse.toVegkartKortform()}`
                : vegsystemreferanse.toVegkartKortform(),
            description: 'Vegsystemreferanse',
            searchText: searchText,
            action: dispatch => {
                dispatch(addVSRToSearch(vegsystemreferanse));
            },
        };
    }
};

export const createSearchSuggestionLinkQuery = (searchText: string): SearchSuggestion => {
    return {
        title: searchText,
        description: 'Veglenkefilter',
        searchText: searchText,
        action: dispatch => {
            dispatch(addLinkQuery(searchText));
        },
    };
};

export const createSearchSuggestionVegsystemreferanseFromLink = (
    searchText: string
): SearchSuggestion => {
    return {
        title: searchText,
        description: 'Vis vegsystemreferanse for veglenkeposisjon',
        searchText: searchText,
        action: dispatch => {
            dispatch(getVSRForVeglenkesekvensAndZoom(searchText));
        },
    };
};

export const createSearchSuggestionHistoricalVegreferanse = (
    vegreferanse: LegacyVegreferanse
): SearchSuggestion => {
    return {
        title: vegreferanse.kortform,
        description: 'Vis vegsystemreferanse fra historisk vegreferanse',
        searchText: vegreferanse.kortform,
        pendingText: `Henter vegsystemreferanse fra historisk vegreferanse ${vegreferanse.kortform}`,
        action: dispatch => {
            return dispatch(getVSRForLegacyVegreferanse(vegreferanse));
        },
    };
};

export const createSearchSuggestionVegobjekttype = (
    searchText: string,
    vegobjekttype: Vegobjekttype,
    isLoggedIn: boolean
): SearchSuggestion => {
    const { subtitle, disabled } = vegobjekttypeToEntry(
        vegobjekttype,
        undefined,
        isLoggedIn
    );
    return {
        title: `${vegobjekttype.navn} (${vegobjekttype.id})`,
        description: subtitle ?? '',
        disabled,
        searchText: searchText,
        action: dispatch => {
            dispatch(addVegobjekttypeToSearch(vegobjekttype));
        },
    };
};

export const createSearchSuggestionKommune = (
    searchText: string,
    kommune: Kommune
): SearchSuggestion => {
    return {
        title: `${kommune.navn} (${kommune.nummer})`,
        description: 'Kommunenummer',
        searchText: searchText,
        action: dispatch => {
            dispatch(addKommuneToSearch(kommune));
        },
    };
};

export const createSearchSuggestionRiksvegrute = (
    searchText: string,
    riksvegrute: Riksvegrute
): SearchSuggestion => {
    return {
        title: `${riksvegrute.navn} (${riksvegrute.enumid})`,
        description: 'Riksvegrute',
        searchText: searchText,
        action: dispatch => {
            dispatch(addRiksvegruteToSearch(riksvegrute));
        },
    };
};

export const createSearchSuggestionVegobjekt = (searchText: string): SearchSuggestion => {
    return {
        title: searchText,
        description: 'Vegobjekt',
        searchText: searchText,
        action: dispatch => {
            const vegobjekt = Vegobjekt.dummy(parseInt(searchText.trim(), 10), null);
            dispatch(addVegobjektToSearch(vegobjekt));
        },
    };
};

export const createSearchSuggestionCoordinate = (
    searchText: string
): SearchSuggestion => {
    return {
        title: searchText,
        description: 'Koordinater (Nord, Øst)',
        searchText: searchText,
        action: dispatch => {
            const { north, east } = parseCoordinates(searchText);
            dispatch(zoomToExtent(kartutsnittFromWktStrings(`POINT(${east} ${north})`)));
            dispatch(
                addPointToMap({
                    id: uuidgen.v4(),
                    geometry: [Number(east), Number(north)],
                })
            );
        },
    };
};

export const createSearchSuggestionVegsystemreferanseFromCoordinate = (
    searchText: string
): SearchSuggestion => {
    return {
        title: searchText,
        description: 'Vis vegsystemreferanse for koordinat',
        searchText: searchText,
        action: dispatch => {
            const { north, east } = parseCoordinates(searchText);
            dispatch(getVSRForCoordinateAndZoom([Number(east), Number(north)]));
        },
    };
};

const isNumber = (n: string | number): boolean =>
    !isNaN(parseFloat(String(n))) && isFinite(Number(n));

export const isCoordinate = (s: string): boolean => !!extractCoordinateOrdinateTexts(s);

export const isPointLinkQuery = (s: string): boolean =>
    /^(?:0(?:\.\d+)?|1(?:\.0+)?)@\d+$/.test(s.trim());

// When coordinates are bounded to Norway, the range of valid values for the north ordinate does not intersect
// with the range of valid values for the east ordinate. This rule applies for both UTM33 and WGS84.
// North ordinates will always be greater than a certain cutoff and east ordinates will always be lesser.
function reorderCoordinates(coordinates: Position, projection: Projection): Position {
    const [first, second] = coordinates;
    const cutoff = projection === Projection.WGS84 ? 40 : 1000000;

    let north: number, east: number;
    if (first > cutoff && second < cutoff) {
        north = first;
        east = second;
    } else if (second > cutoff && first < cutoff) {
        east = first;
        north = second;
    } else {
        north = first;
        east = second;
    }
    return [north, east];
}

// Expects and returns coordinates in north/east order
function projectWGS84ToUTM33(coordinates: Position) {
    const from =
        '+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees';
    const to = '+proj=utm +zone=33 +ellps=GRS80 +units=m +wktext +no_defs';
    return flip(proj4(from, to, flip(coordinates)));
}

const inWgs84Range = (ordinate: number) => ordinate >= 0 && ordinate < 90;
const flip = (coords: Position): Position => [coords[1], coords[0]];
